import { Controller } from "@hotwired/stimulus";

/**
 * The ModifierGroupsFilterController handles real-time filtering of modifier groups.
 * It provides search functionality that:
 * - Filters modifier groups by name
 * - Filters modifiers within groups
 * - Shows/hides a clear button
 * - Updates group visibility based on matching content
 * - Shows a no results state when no matches are found in a section
 */
export default class extends Controller {
  static targets = [
    "searchInput",
    "clearButton",
    "section",
    "group",
    "groupName",
    "modifier",
    "noResults",
  ];

  connect() {
    this.toggleClearButton();
    document.addEventListener("keydown", this.exitSearchMode);
  }

  disconnect() {
    document.removeEventListener("keydown", this.exitSearchMode);
  }

  exitSearchMode = (event) => {
    if (event.key === "Escape") {
      this.clearSearch();
    }
  };

  /**
   * Normalizes text by removing accents and converting to lowercase.
   * This allows for accent-insensitive search (e.g., "cafe" matches "café").
   */
  normalizeText(text) {
    return text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  /**
   * Main filtering function that:
   * 1. Filters modifier groups based on their name
   * 2. Filters modifiers within groups
   * 3. Shows groups if their name matches or they have matching modifiers
   * 4. Updates clear button visibility
   * 5. Shows/hides the no results state for each section independently
   */
  filterGroups() {
    const searchTerm = this.normalizeText(this.searchInputTarget.value.trim());
    this.toggleClearButton();

    // Process each section independently
    this.sectionTargets.forEach((section) => {
      let hasVisibleGroups = false;

      // Get all groups in this section
      const groups = section.querySelectorAll(
        '[data-modifier-groups-filter-target="group"]'
      );

      groups.forEach((group) => {
        const groupName = this.normalizeText(
          group.querySelector(
            '[data-modifier-groups-filter-target="groupName"]'
          )?.textContent || ""
        );

        let hasVisibleModifiers = false;
        const groupNameMatches = groupName.includes(searchTerm);

        // Get all modifiers in this group
        const modifiers = group.querySelectorAll(
          '[data-modifier-groups-filter-target="modifier"]'
        );

        // If group name matches, show all modifiers
        if (groupNameMatches) {
          modifiers.forEach((modifier) => {
            modifier.classList.remove("hidden");
          });
          hasVisibleModifiers = true;
        } else {
          // Otherwise, filter modifiers
          modifiers.forEach((modifier) => {
            const modifierName = this.normalizeText(modifier.textContent);
            const isVisible = modifierName.includes(searchTerm);
            modifier.classList.toggle("hidden", !isVisible);
            if (isVisible) hasVisibleModifiers = true;
          });
        }

        // Show group if name matches or it has visible modifiers
        const isGroupVisible = hasVisibleModifiers || groupNameMatches;
        group.classList.toggle("hidden", !isGroupVisible);
        if (isGroupVisible) hasVisibleGroups = true;
      });

      // Show/hide no results state for this section
      const noResultsElement = section.querySelector(
        '[data-modifier-groups-filter-target="noResults"]'
      );
      if (noResultsElement) {
        noResultsElement.classList.toggle(
          "hidden",
          hasVisibleGroups || !searchTerm
        );
      }
    });
  }

  /**
   * Clears the search input and resets the filter
   */
  clearSearch() {
    this.searchInputTarget.value = "";
    this.filterGroups();
    this.searchInputTarget.blur();
  }

  /**
   * Toggles the visibility of the clear button based on whether
   * there is text in the search input or the input is focused.
   */
  toggleClearButton() {
    const hasText = this.searchInputTarget.value.trim().length > 0;
    const isFocused = document.activeElement === this.searchInputTarget;
    const shouldShow = hasText || isFocused;

    this.clearButtonTarget.classList.toggle("opacity-0", !shouldShow);
    this.clearButtonTarget.classList.toggle("pointer-events-none", !shouldShow);
  }

  showClearButtonOnFocus() {
    this.toggleClearButton();
  }

  hideClearButtonOnBlur() {
    this.toggleClearButton();
  }

  clearSearchWithoutFocus(event) {
    event.preventDefault();
    event.stopPropagation();

    const wasFocused = document.activeElement === this.searchInputTarget;
    const hadText = this.searchInputTarget.value.trim().length > 0;

    this.searchInputTarget.value = "";
    this.filterGroups();

    if (wasFocused && hadText) {
      this.searchInputTarget.focus();
    } else if (wasFocused && !hadText) {
      this.searchInputTarget.blur();
    } else {
      this.toggleClearButton();
    }
  }
}
